import React, { useState } from "react";
import { Modal, Table, Spin, Tag, Input, Alert } from "antd";
import { useQuery, useMutation } from "@apollo/client";

import { Button } from "../../../components/buttons/buttons";
import { GET_INVOICE } from "../../../graphql/InvoiceQueries";
import { CREATE_CREDIT_NOTE } from "../../../graphql/InvoiceMutations";
import { stiahniZbernuFakturu, posliZbernuFakturu } from "../../../Api/zbernaFaktura";
import displayNotification from "../../reservation/admin/notification/notification";
import { formatPrice, formatSQLDate } from "../../../utils/common";

/**
 * DETAIL ZBERNEJ FAKTURY V OKNE ZAKAZIEK.
 *
 * Otvori sa klikom na cislo faktury v zozname. Nie je to obrazovka, len
 * okno: ukaze, co na doklade je, stiahne PDF, posle ho zakaznikovi s kopiou
 * uctovnickej a vie vystavit dobropis. Dobropis je jedina cesta, ako doklad
 * opravit, lebo riadky su po vystaveni zmrazene a zakazka zamknuta.
 */

/** formatSQLDate z prazdneho datumu vyrobi "Invalid Date", tak to chytime tu. */
const datum = (d) => (d ? formatSQLDate(d) : "");

const Riadok = ({ popis, hodnota }) =>
  hodnota === null || hodnota === undefined || hodnota === "" ? null : (
    <div style={{ display: "flex", gap: "8px", padding: "2px 0" }}>
      <span style={{ minWidth: "160px", color: "#8c90a4" }}>{popis}</span>
      <span>{hodnota}</span>
    </div>
  );

const ZbernaFakturaModal = (props) => {
  const { invoiceId, onClose, onZmena } = props;

  const [robim, setRobim] = useState(null);
  const [dobropisOtvoreny, setDobropisOtvoreny] = useState(false);
  const [dovod, setDovod] = useState("");

  const { data, loading, refetch } = useQuery(GET_INVOICE, {
    variables: {
      tokenGet: process.env.REACT_APP_TOKEN_GET,
      id: invoiceId,
    },
    skip: !invoiceId,
    fetchPolicy: "network-only",
  });

  const [vystavDobropis, { loading: bezimDobropis }] = useMutation(CREATE_CREDIT_NOTE);

  const faktura = data?.getInvoice;
  const jeDobropis = faktura?.type === "credit_note";

  const stiahni = async () => {
    setRobim("pdf");
    const v = await stiahniZbernuFakturu(faktura.id, faktura.number);
    setRobim(null);
    if (!v.status) displayNotification("error", "Faktúra", "PDF sa nepodarilo vytvoriť");
  };

  const posli = async () => {
    const ok = window.confirm(
      `Poslať faktúru ${faktura.number} zákazníkovi, s kópiou účtovníčke?`
    );
    if (!ok) return;
    setRobim("mail");
    const v = await posliZbernuFakturu(faktura.id);
    setRobim(null);
    if (v.status && String(v.data?.sucessful) !== "false") {
      displayNotification("success", "Faktúra", `Faktúra ${faktura.number} odoslaná`);
      refetch();
    } else {
      const sprava =
        v?.error?.response?.data?.message ||
        v?.data?.message ||
        "Faktúra nebola odoslaná";
      displayNotification("error", "Faktúra", sprava);
    }
  };

  const dobropisuj = async () => {
    try {
      const { data: odpoved } = await vystavDobropis({
        variables: {
          tokenMutation: process.env.REACT_APP_TOKEN_MUTATION,
          invoiceId: Number(faktura.id),
          reason: dovod || undefined,
        },
      });
      const v = odpoved?.createCreditNote;
      if (String(v?.sucessful) === "false") {
        displayNotification("error", "Dobropis", v?.message || "Dobropis zlyhal");
        return;
      }
      displayNotification(
        "success",
        "Dobropis",
        `Dobropis ${v?.number} vystavený, zákazky sú znova voľné`
      );
      setDobropisOtvoreny(false);
      setDovod("");
      if (typeof onZmena === "function") onZmena();
      onClose();
    } catch (chyba) {
      displayNotification("error", "Dobropis", chyba?.message || "Dobropis zlyhal");
    }
  };

  return (
    <>
      <Modal
        title={
          faktura
            ? `${jeDobropis ? "Dobropis" : "Zberná faktúra"} ${faktura.number}`
            : "Faktúra"
        }
        visible={Boolean(invoiceId)}
        onCancel={onClose}
        width={900}
        footer={[
          !jeDobropis && faktura && faktura.status !== "cancelled" && (
            <Button
              key="dobropis"
              type="warning"
              onClick={() => setDobropisOtvoreny(true)}
            >
              Vystaviť dobropis
            </Button>
          ),
          <Button
            key="pdf"
            type="light"
            disabled={!faktura || robim === "pdf"}
            onClick={stiahni}
          >
            {robim === "pdf" ? "Pripravujem..." : "Stiahnuť PDF"}
          </Button>,
          <Button
            key="mail"
            type="primary"
            disabled={!faktura || robim === "mail"}
            onClick={posli}
          >
            {robim === "mail" ? "Posielam..." : "Poslať mailom"}
          </Button>,
          <Button key="zavri" type="default" onClick={onClose}>
            Zavrieť
          </Button>,
        ].filter(Boolean)}
      >
        {loading && !faktura && (
          <div className="spin" style={{ textAlign: "center", padding: "30px" }}>
            <Spin />
          </div>
        )}
        {faktura && (
          <>
            <div style={{ marginBottom: "16px" }}>
              <Riadok
                popis="Stav"
                hodnota={
                  <Tag color={faktura.status === "cancelled" ? "red" : "green"}>
                    {faktura.status === "cancelled" ? "Zrušená dobropisom" : "Vystavená"}
                  </Tag>
                }
              />
              <Riadok popis="Odberateľ" hodnota={faktura.customer_company} />
              <Riadok popis="Vystavená" hodnota={datum(faktura.issue_date)} />
              <Riadok popis="Splatnosť" hodnota={datum(faktura.due_date)} />
              <Riadok
                popis="Dodanie"
                hodnota={datum(faktura.delivery_date)}
              />
              <Riadok
                popis="Obdobie"
                hodnota={
                  faktura.period_from && faktura.period_to
                    ? `${datum(faktura.period_from)} až ${datum(faktura.period_to)}`
                    : ""
                }
              />
              <Riadok
                popis="Platba"
                hodnota={faktura.payment_type === "bank" ? "Bankový prevod" : "Hotovosť"}
              />
              <Riadok
                popis="Spolu"
                hodnota={
                  <strong>
                    {formatPrice(Number(faktura.total_gross || 0))} s DPH, z toho základ{" "}
                    {formatPrice(Number(faktura.total_netto || 0))} a DPH{" "}
                    {formatPrice(Number(faktura.total_vat || 0))}
                  </strong>
                }
              />
              <Riadok
                popis="Fakturačný mail"
                hodnota={faktura.carOwner?.invoice_emails}
              />
              <Riadok
                popis="Odoslaná zákazníkovi"
                hodnota={
                  faktura.sent_to_customer_at
                    ? datum(faktura.sent_to_customer_at)
                    : "zatiaľ neodoslaná"
                }
              />
              <Riadok
                popis="Odoslaná účtovníčke"
                hodnota={
                  faktura.sent_to_accountant_at
                    ? datum(faktura.sent_to_accountant_at)
                    : "zatiaľ neodoslaná"
                }
              />
            </div>

            {!faktura.carOwner?.invoice_emails && (
              <Alert
                style={{ marginBottom: "16px" }}
                type="warning"
                showIcon
                message="Majiteľ nemá na karte fakturačný mail, poslať sa nedá. Doplň ho v Majiteľoch."
              />
            )}

            <Table
              size="small"
              pagination={false}
              scroll={{ x: "max-content" }}
              rowKey={(r) => r.id}
              dataSource={faktura.items || []}
              columns={[
                { title: "Zákazka", dataIndex: "serviceId", key: "serviceId" },
                { title: "EČV", dataIndex: "licence_plate", key: "licence_plate" },
                {
                  title: "Dátum",
                  dataIndex: "service_date",
                  key: "service_date",
                  render: (d) => datum(d),
                },
                { title: "Označenie", dataIndex: "name", key: "name" },
                { title: "Kód", dataIndex: "code", key: "code" },
                { title: "Počet", dataIndex: "qty", key: "qty" },
                { title: "m.j.", dataIndex: "unit", key: "unit" },
                {
                  title: "Cena za m.j.",
                  dataIndex: "price_netto",
                  key: "price_netto",
                  render: (c) => formatPrice(Number(c || 0)),
                },
                {
                  title: "DPH %",
                  dataIndex: "vat_rate",
                  key: "vat_rate",
                },
                {
                  title: "Bez DPH",
                  dataIndex: "total_netto",
                  key: "total_netto",
                  render: (c) => formatPrice(Number(c || 0)),
                },
                {
                  title: "Spolu",
                  dataIndex: "total_gross",
                  key: "total_gross",
                  render: (c) => formatPrice(Number(c || 0)),
                },
              ]}
            />
          </>
        )}
      </Modal>

      <Modal
        title="Vystaviť dobropis"
        visible={dobropisOtvoreny}
        onCancel={() => setDobropisOtvoreny(false)}
        footer={[
          <Button
            key="zrus"
            type="light"
            onClick={() => setDobropisOtvoreny(false)}
            disabled={bezimDobropis}
          >
            Zrušiť
          </Button>,
          <Button
            key="ok"
            type="warning"
            onClick={dobropisuj}
            disabled={bezimDobropis}
          >
            {bezimDobropis ? "Vystavujem..." : "Vystaviť dobropis"}
          </Button>,
        ]}
      >
        <p>
          Dobropis zruší faktúru {faktura?.number} v plnej výške, vezme ďalšie číslo
          z toho istého radu a zákazky uvolní, takže sa dajú vyfakturovať znova.
          Vrátiť sa to nedá.
        </p>
        <Input.TextArea
          rows={3}
          value={dovod}
          onChange={(e) => setDovod(e.target.value)}
          placeholder="Dôvod dobropisu, napríklad nesprávny odberateľ"
        />
      </Modal>
    </>
  );
};

export default ZbernaFakturaModal;
